/*
  rgd-kit-pronto.js — interatividade da seção Kit pronto.

  Composição fixa: o lojista escolhe o produto-rótulo (parent) e os
  componentes direto no editor de temas (blocks). Aqui só há seleção de
  variante por item, resumo de exibição e o /cart/add.js que monta a mesma
  estrutura pai+filhos usada pelo Monte seu Kit (rgd-kit-builder.js):
  um item pai com properties (_kit_id, Kit) e cada componente como filho
  com parent_id, sem properties próprias — parent_relationship já identifica
  os filhos nativamente.

  discount_pct é só exibição (RGDKit.formatMoney faz a matemática de mostrar
  o total estimado). Nenhum código aqui cria desconto de verdade: quem
  desconta é o desconto automático configurado no Admin.
*/
(function () {
  'use strict';

  class RGDKitPronto {
    constructor(root) {
      this.root = root;
      this.parentVariantId = Number(root.dataset.parentId) || null;
      this.discountPct = Number(root.dataset.discountPct) || 0;
      this.label = root.dataset.kitLabel || '';
      this.moneyOpts = {
        locale: root.dataset.locale || 'pt-BR',
        currency: root.dataset.currency || 'BRL'
      };

      this.items = Array.from(root.querySelectorAll('[data-rkp-item]'));
      this.els = {
        subtotal: root.querySelector('[data-rkp-subtotal]'),
        discount: root.querySelector('[data-rkp-discount]'),
        total: root.querySelector('[data-rkp-total]'),
        add: root.querySelector('[data-rkp-add]'),
        error: root.querySelector('[data-rkp-error]')
      };

      // Guardado a cada render() para o evento de analytics no submit não
      // precisar recalcular (e não depender de RGDKit já ter carregado).
      this.lastTotal = 0;

      this.bind();
      this.render();
    }

    bind() {
      this.items.forEach((item) => {
        const select = item.querySelector('[data-rkp-variant]');
        if (select) select.addEventListener('change', () => this.render());
      });

      if (this.els.add) this.els.add.addEventListener('click', () => this.submit());
    }

    variantsOf(item) {
      try {
        return JSON.parse(item.dataset.variants) || [];
      } catch (e) {
        return [];
      }
    }

    selectedVariant(item) {
      const variants = this.variantsOf(item);
      const select = item.querySelector('[data-rkp-variant]');
      if (select) {
        const id = Number(select.value);
        return variants.find((variant) => variant.id === id) || null;
      }
      // Sem select: produto de variante única, é a única opção possível.
      return variants[0] || null;
    }

    quantityOf(item) {
      const qty = Number(item.dataset.quantity);
      return Number.isFinite(qty) && qty > 0 ? qty : 1;
    }

    getSelection() {
      return this.items
        .map((item) => {
          const variant = this.selectedVariant(item);
          if (!variant) return null;
          return {
            handle: item.dataset.handle,
            title: item.dataset.title,
            variantId: variant.id,
            variantTitle: variant.title,
            priceCents: Number(variant.price) || 0,
            quantity: this.quantityOf(item)
          };
        })
        .filter(Boolean);
    }

    pieceCount(selection) {
      return selection.reduce((sum, item) => sum + item.quantity, 0);
    }

    // Recalcula o resumo a cada troca de variante. A matemática é local
    // (preço x quantidade), só a formatação vem de RGDKit — sem ele
    // carregado (ordem de scripts quebrada, por exemplo) não há como
    // exibir moeda de forma confiável, então a seção não tenta.
    //
    // Subtotal é o preço padrão dos produtos e desconto é o valor que o
    // desconto automático do Admin (espelhado em discount_pct) tira dele —
    // compare_at_price fica de fora de propósito.
    render() {
      if (typeof RGDKit === 'undefined') return;

      const selection = this.getSelection();
      const money = (cents) => RGDKit.formatMoney(cents, this.moneyOpts);

      const subtotal = selection.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);

      // Linha a linha, arredondando o PREÇO FINAL de cada uma — não o
      // desconto, e não o total somado. É a regra do carrinho: 11990 a 5%
      // vira round(11390,5) = 11391, ou seja R$113,91, o mesmo que aparece
      // no drawer. Arredondar o desconto agregado erra por centavos e faz a
      // seção prometer um total que o carrinho não confirma.
      const discount = selection.reduce((sum, item) => {
        const lineFull = item.priceCents * item.quantity;
        const lineFinal = Math.round((lineFull * (100 - this.discountPct)) / 100);
        return sum + (lineFull - lineFinal);
      }, 0);

      const total = subtotal - discount;
      this.lastTotal = total;

      if (this.els.subtotal) this.els.subtotal.textContent = money(subtotal);
      if (this.els.discount) {
        this.els.discount.textContent = discount ? '− ' + money(discount) + ' (' + this.discountPct + '%)' : '—';
      }
      if (this.els.total) this.els.total.textContent = money(total);
    }

    kitId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      return 'kit-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }

    showError(message) {
      if (!this.els.error) return;
      this.els.error.textContent = message;
      this.els.error.hidden = false;
    }

    clearError() {
      if (!this.els.error) return;
      this.els.error.textContent = '';
      this.els.error.hidden = true;
    }

    // Revalida disponibilidade antes de enviar: uma requisição por produto
    // distinto, em paralelo, só no clique.
    async validateStock(selection) {
      const handles = Array.from(new Set(selection.map((item) => item.handle).filter(Boolean)));
      const products = await Promise.all(
        handles.map((handle) =>
          fetch(window.Shopify.routes.root + 'products/' + handle + '.js')
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null)
        )
      );

      const availability = new Map();
      products.forEach((product) => {
        if (!product) return;
        product.variants.forEach((variant) => availability.set(variant.id, variant.available));
      });

      return selection.filter((item) => availability.get(item.variantId) === false);
    }

    async submit() {
      const selection = this.getSelection();
      if (!selection.length) return;

      if (!this.parentVariantId) {
        this.showError('Kit não configurado: falta o produto-rótulo.');
        return;
      }

      this.clearError();
      this.els.add.disabled = true;
      const originalText = this.els.add.textContent;
      this.els.add.textContent = 'Adicionando...';

      // Marca a fronteira: depois que o carrinho confirma, nenhuma falha de
      // UI pode ser reportada como "não foi possível adicionar" — foi
      // exatamente esse bug (duplicar o add) que este projeto já corrigiu.
      let added = false;

      try {
        const unavailable = await this.validateStock(selection);
        if (unavailable.length) {
          const names = unavailable
            .map((item) => item.title + ' (' + item.variantTitle + ')')
            .join(', ');
          this.showError('Esgotou enquanto você via este kit: ' + names + '. Atualize a página para continuar.');
          return;
        }

        const kitId = this.kitId();

        // O pai carrega a identidade do kit. Duas linhas do mesmo variante
        // com propriedades diferentes não se fundem, e é isso que garante
        // que dois kits no carrinho tenham dois pais distintos.
        const items = [
          {
            id: this.parentVariantId,
            quantity: 1,
            properties: { _kit_id: kitId, Kit: this.label }
          }
        ].concat(
          selection.map((item) => ({
            id: item.variantId,
            quantity: item.quantity,
            parent_id: this.parentVariantId
          }))
        );

        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          this.showError(payload.description || 'Não foi possível adicionar o kit. Tente novamente.');
          return;
        }

        // Daqui em diante o kit ESTÁ no carrinho. Atualizar o drawer é outro
        // domínio de falha e não pode compartilhar o catch do add: reportar
        // "falhou" depois do sucesso faz o cliente adicionar o kit de novo.
        added = true;

        document.dispatchEvent(
          new CustomEvent('rgd:kit', {
            detail: {
              action: 'add_to_cart',
              kitSlug: null,
              kitQty: this.pieceCount(selection),
              kitValue: this.lastTotal
            }
          })
        );

        try {
          await this.refreshCart();
        } catch (refreshError) {
          window.location.href = window.Shopify.routes.root + 'cart';
        }
      } catch (error) {
        // Se "added" já é true, o /cart/add.js confirmou e a falha é de
        // outro domínio (ex.: dispatchEvent) — reportar "não foi possível
        // adicionar" aqui duplicaria a mensagem sobre um kit que já entrou.
        if (!added) this.showError('Não foi possível adicionar o kit. Tente novamente.');
      } finally {
        // Botão sempre volta ao estado normal, sucesso ou não: é uma
        // composição fixa, nada impede adicionar o mesmo kit de novo.
        this.els.add.disabled = false;
        this.els.add.textContent = originalText;
      }
    }

    async refreshCart() {
      const response = await fetch(window.Shopify.routes.root + '?sections=cart-drawer,cart-icon-bubble');
      if (!response.ok) {
        window.location.href = window.Shopify.routes.root + 'cart';
        return;
      }
      const data = await response.json();

      const cartIcon = document.getElementById('cart-icon-bubble');
      if (cartIcon && data['cart-icon-bubble']) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = data['cart-icon-bubble'];
        const fresh = wrapper.querySelector('#cart-icon-bubble');
        if (fresh) cartIcon.innerHTML = fresh.innerHTML;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || !data['cart-drawer']) {
        window.location.href = window.Shopify.routes.root + 'cart';
        return;
      }

      const doc = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
      const fresh = doc.querySelector('cart-drawer');
      if (fresh) cartDrawer.innerHTML = fresh.innerHTML;
      cartDrawer.classList.remove('is-empty');

      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        cartDrawer.classList.add('active', 'animate');
        document.body.classList.add('overflow-hidden');
      }

      document.dispatchEvent(new CustomEvent('rgd:cart-updated'));
    }
  }

  window.RGDKitPronto = RGDKitPronto;

  function initKitsProntos(scope) {
    (scope || document).querySelectorAll('[data-rgd-kit-pronto]').forEach((root) => {
      if (root.dataset.rkpReady) return;
      root.dataset.rkpReady = 'true';
      new RGDKitPronto(root);
    });
  }

  document.addEventListener('DOMContentLoaded', () => initKitsProntos(document));

  // No editor de temas a Shopify troca o DOM da section por AJAX e dispara
  // shopify:section:load — sem isto a seção fica inerte depois de qualquer
  // mudança de setting. Mesmo padrão de rgd-kit-builder.js.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => initKitsProntos(event.target));
  }
})();
